import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import {
  GenerateSummaryReportRequest,
  GetTGOReportResponse,
  ReportHistory,
  TGOReportDownloadV3,
  TGOReportV3,
} from "../models/TGOReport";
import { GenerateReportForm } from "../pages/report/summary/SummaryGenerateReport";
import { Dayjs } from "dayjs";

type ReportType = "tgo" | "summary";

export type ReportFormat = "excel" | "word";

// File extension per report format — used to name downloads after the report name.
export const REPORT_FORMAT_EXTENSION: Record<ReportFormat, string> = {
  excel: "xlsx",
  word: "docx",
};

// Click a (hidden) download-anchor. No target="_blank" — opening new tabs gets the
// 2nd+ file popup-blocked, whereas download-anchors don't count as popups.
const triggerAnchorDownload = (href: string, filename?: string) => {
  const link = document.createElement("a");
  link.href = href;
  if (filename) link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// v3 reports hand back presigned S3 URLs. A cross-origin URL makes the browser
// ignore the <a download> name and fall back to the S3 object-key name, so to
// honor the user's report name we fetch the file into a same-origin blob URL,
// where the chosen filename IS respected. If the fetch is blocked (e.g. CORS) we
// fall back to a direct download so the file still arrives — with the server name.
export const downloadFromUrl = async (url: string, filename?: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed: HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerAnchorDownload(objectUrl, filename);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  } catch {
    triggerAnchorDownload(url, filename);
  }
};

// Download several files at once (e.g. excel + word from one generate). Clicks are
// staggered so the browser keeps them within its multi-download allowance instead
// of dropping all but the first.
export const downloadFiles = (
  files: { downloadUrl: string; filename: string }[],
) => {
  files.forEach((file, index) => {
    setTimeout(
      () => downloadFromUrl(file.downloadUrl, file.filename),
      index * 600,
    );
  });
};

export interface GenerateTGOReportForm {
  reportName: string;
  formats: ReportFormat[];
  startDate: Dayjs;
  endDate: Dayjs;
  startDateBaseYear: Dayjs | null;
  endDateBaseYear: Dayjs | null;
  status: string[];
  contact: string;
  productivity: number;
  baseProductivity: number;
  productivityUnit: string;
  logo: string;
  map: string;
  chart: string;
  flowchart: string;
  suborganizations: string[];
}

export const ReportService = {
  getReport: async (
    reportType: ReportType,
  ): Promise<ReportHistory[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get(`/v3/reports/${reportType}`);
      const data: GetTGOReportResponse = response.data;
      return data.results.reportHistories;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  generateTGOReport: async (
    formData: GenerateTGOReportForm,
  ): Promise<TGOReportV3 | null> => {
    const extractImage = (dataUrl?: string) => {
      if (!dataUrl) return null;
      const [meta, base64] = dataUrl.split(",");
      const extension = meta?.split("/")[1]?.split(";")[0];
      return base64 && extension ? { base64, extension } : null;
    };

    const formats =
      formData.formats && formData.formats.length > 0
        ? formData.formats
        : ["excel"];

    const body: Record<string, any> = {
      reportName: formData.reportName,
      formats,
      reportStartDate: formData.startDate.format("YYYY-MM-DD"),
      reportEndDate: formData.endDate.format("YYYY-MM-DD"),
      status: formData.status,
      suborganizations: formData.suborganizations,
    };

    const logoData = extractImage(formData.logo);
    if (logoData) {
      body.companyLogo = logoData.base64;
      body.companyLogoExtension = logoData.extension;
    }

    const mapData = extractImage(formData.map);
    if (mapData) {
      body.organizationMap = mapData.base64;
      body.organizationMapExtension = mapData.extension;
    }

    const chartData = extractImage(formData.chart);
    if (chartData) {
      body.organizationChart = chartData.base64;
      body.organizationChartExtension = chartData.extension;
    }

    // Process-flow diagram — Word-only (§3.1.3), added as a 4th image when the
    // user has uploaded one. v3 names this field processFlowDiagram.
    if (formats.includes("word")) {
      const flowchartData = extractImage(formData.flowchart);
      if (flowchartData) {
        body.processFlowDiagram = flowchartData.base64;
        body.processFlowDiagramExtension = flowchartData.extension;
      }
    }

    if (formData.contact) body.contactLocation = formData.contact;
    if (formData.productivityUnit)
      body.productivityUnit = formData.productivityUnit;
    if (formData.productivity) body.productivity = formData.productivity;
    if (formData.baseProductivity)
      body.baseProductivity = formData.baseProductivity;

    if (formData.startDateBaseYear) {
      body.baseReportStartDate =
        formData.startDateBaseYear.format("YYYY-MM-DD");
    }
    if (formData.endDateBaseYear) {
      body.baseReportEndDate = formData.endDateBaseYear.format("YYYY-MM-DD");
    }

    try {
      alertService.showLoading();
      const response = await apiClient.post<{ results: TGOReportV3 }>(
        "/v3/reports/tgo",
        body,
        { _longRunning: true } as any,
      );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  downloadReport: async (
    refId: string,
    reportType: ReportType,
    format: ReportFormat,
  ): Promise<TGOReportDownloadV3 | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post<{ results: TGOReportDownloadV3 }>(
        `/v3/reports/${reportType}/${refId}`,
        { format },
      );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  generateSummaryReport: async (
    formData: GenerateReportForm,
  ): Promise<TGOReportV3 | null> => {
    const body: GenerateSummaryReportRequest = {
      reportStartDate: formData.startDate.format("YYYY-MM-DD"),
      reportEndDate: formData.endDate.format("YYYY-MM-DD"),
      reportName: formData.reportName,
      suborganizations: formData.subOrganization,
    };

    try {
      alertService.showLoading();
      const response = await apiClient.post<{ results: TGOReportV3 }>(
        "/v3/reports/summary",
        body,
        { _longRunning: true } as any,
      );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
};

const handleError = (error: unknown) => handleApiError(error);
